import { UserTypes } from 'store/auth/types'
import { routes } from 'types/routes'
import { checkIsSales } from 'utils/sales'
import { USER_TYPE_CONFIG } from 'constants/userTypes'
import { capitalize } from './format.ts'
import { store } from 'store/index.ts'
import { getContactByEmail, getMyContact } from 'store/contact/thunks'
import { unwrapResult } from '@reduxjs/toolkit'
import { GetContactSuccessPayload } from 'store/contact/thunks/types.ts'
import { AddressFormats } from 'types/format.ts'
import posthog from 'posthog-js'
import { NODE_ENV } from 'constants/envs.ts'
import { identifyUser, trackEvent } from 'utils/tracking.ts'
import { isActiveContract } from 'utils/contracts.ts'
import { Address, getProductFromProductCode } from '@boltenergy-be/shared'
import { AccountRecordTypeIds } from 'types/contacts.ts'

/**
 * returns the correct route of the user types' homepage
 *
 * @param {UserTypes} type
 * @return {string}
 */
export const getUserTypeHomepage = (type: UserTypes | null): string => {
  const { selected } = store.getState().contact
  switch (type) {
    case UserTypes.SALES:
      return USER_TYPE_CONFIG[UserTypes.SALES].home
    case UserTypes.SUPER_USER:
      return USER_TYPE_CONFIG[UserTypes.SUPER_USER].home
    case UserTypes.CUSTOMER:
      return selected.account && selected.billingContract ? routes.OVERVIEW : routes.ACCOUNT_SELECTION
    default:
      return checkIsSales() ? routes.LOGIN_SALES : routes.LOGIN
  }
}

/**
 * returns the correct route of the user types' homepage
 *
 * @param {UserTypes} type
 * @return {string}
 */
export const getUserTypeLoginPage = (type: UserTypes | null): string => {
  if (type) return USER_TYPE_CONFIG[type].login
  return checkIsSales() ? USER_TYPE_CONFIG[UserTypes.SALES].login : USER_TYPE_CONFIG[UserTypes.CUSTOMER].login
}

/**
 * Verifies if the given coords are not null/undefined and not of value 0
 * @param {(number | null)[]} coords
 * @returns {boolean}
 */
export const verifyCoordinates = (coords: (number | null)[]): boolean =>
  coords.every((coordinate) => typeof coordinate === 'number' && coordinate !== 0)

/**
 * Formats the given address into a string
 *
 * @param {Address} address
 * @param {AddressFormats} format
 * @returns string|null
 */
export const formatAddress = (address?: Address, format?: AddressFormats): string | null => {
  if (!address || Object.values(address).every((value) => !value)) {
    return null
  }

  const lowercaseStreetName = capitalize(address.streetName)
  const lowercaseTownName = capitalize(address.townName)
  const streetNumberBox = address.streetBox
    ? `${lowercaseStreetName} ${address.streetNumber} ${address.streetBox}`
    : `${lowercaseStreetName} ${address.streetNumber}`
  const postalCodeTown = `${address.postalCode} ${lowercaseTownName}`

  switch (format) {
    case AddressFormats.FULL_TWO_LINES: {
      return `${streetNumberBox}<br />${postalCodeTown}`
    }
    case AddressFormats.STREET_NUMBER_BOX: {
      return streetNumberBox
    }
    case AddressFormats.POSTAL_CODE_TOWN: {
      return postalCodeTown
    }

    case AddressFormats.FULL_SINGLE_LINE:
    default: {
      return `${streetNumberBox}, ${postalCodeTown}`
    }
  }
}

/**
 * Fetches the contact data based on the user type
 * @param {UserTypes.CUSTOMER | UserTypes.SUPER_USER} userType
 * @param {string} contactEmail
 * @returns {Promise<GetContactSuccessPayload | undefined>}
 */
export const fetchContactData = async (
  userType: UserTypes.CUSTOMER | UserTypes.SUPER_USER,
  contactEmail?: string
): Promise<GetContactSuccessPayload | undefined> => {
  try {
    if (userType === UserTypes.SUPER_USER) {
      posthog.opt_out_capturing()
      const email = contactEmail || store.getState().auth.email
      if (email) {
        const res = await store.dispatch(getContactByEmail({ email }))
        return unwrapResult(res)
      }
    } else {
      const res = await store.dispatch(getMyContact())
      const payload = unwrapResult(res)

      trackEvent({ category: 'contact', object: 'fetch_data', action: 'finish' })
      const contact = payload.normalizedStoreData?.contact

      if (contact?.sfId) {
        identifyUser(contact.sfId, {
          email: contact.email,
          _env: NODE_ENV,
          userType,
          activeAddresses:
            Object.values(payload.normalizedStoreData?.billingContracts)?.filter((contract) =>
              isActiveContract(contract?.serviceContracts?.electricity)
            )?.length || 0,
          totalAddresses: Object.keys(payload.normalizedStoreData?.billingContracts)?.length || 0,
          accounts: Object.keys(payload.normalizedStoreData?.accounts)?.length || 0,
          language: contact.language,
          profiles: Object.values(payload.normalizedStoreData?.billingContracts)?.reduce((profiles: Record<string, unknown>, contract) => {
            if (!contract) return profiles

            const { electricity } = contract.serviceContracts
            profiles[contract?.contractNumber] = {
              deliveryMode: contract.deliveryMode,
              dynamicTariff: !!electricity.detail.dynamicTariff,
              isActiveContract: isActiveContract(electricity),
              isProducer: payload.normalizedStoreData?.accounts?.[contract.customerNumber]?.recordType === AccountRecordTypeIds.PRODUCER,
              meterType: electricity.deliveryPoint.meterType,
              monthlyBilling: !!electricity.detail.monthlyBilling,
              producer: electricity.detail.producer,
              product: getProductFromProductCode(electricity.detail.productCode),
              region: electricity.deliveryPoint.region,
              status: electricity.detail.status,
              subscriptionDate: electricity.detail.subscriptionDate,
              type: contract.serviceContracts.gas ? 'gas+electricity' : 'electricity',
              meteringRegime: electricity.deliveryPoint.meteringRegime,
              direction: electricity.deliveryPoint.direction
            }

            return profiles
          }, {})
        })
      }

      return payload
    }

    return undefined
  } catch (error) {
    trackEvent({ category: 'contact', object: 'fetch_data', action: 'fail' }, { userType })
    return undefined
  }
}
